<template>
  <section :class="$style.root">
    <simple-table
      v-if="filteredLabels.length"
      :data="filteredLabels"
      :colums="columns"
      data-testid="labels-table"
      @row-click="handleRowClick"
    >
      <template slot="row-cell" slot-scope="row">
        <div v-if="row.columnId === 'preset'">
          <span :class="$style.presetName">
            {{ getPresetName(row.data.preset_id) }}
          </span>
        </div>
        <div v-else-if="row.columnId === 'labels'">
          <div :class="$style.labelsContainer">
            <span
              v-for="(label, index) in row.data.labels_list"
              :key="index"
              :class="$style.labelTag"
            >
              {{ label }}
            </span>
            <span v-if="!row.data.labels_list.length" :class="$style.noLabels">
              Нет меток
            </span>
          </div>
        </div>
        <div v-else-if="row.columnId === 'created_at'">
          <span :class="$style.dateText">
            {{ formatDate(row.data.created_at) }}
          </span>
        </div>
        <div v-else-if="row.columnId === 'actions'">
          <div :class="$style.actions">
            <button
              :key="`edit-${row.data.id}`"
              :class="$style.actionBtn"
              :data-testid="`edit-btn-${row.data.id}`"
              title="Редактировать"
              @click.stop="handleEdit(row.data)"
            >
              ✎
            </button>
            <button
              :key="`delete-${row.data.id}`"
              :class="$style.actionBtn"
              :data-testid="`delete-btn-${row.data.id}`"
              title="Удалить"
              @click.stop="handleDelete(row.data)"
            >
              ×
            </button>
          </div>
        </div>
      </template>
    </simple-table>

    <div v-else-if="!isLoading" :class="$style.emptyState">
      <icon name="website" :class="$style.emptyIcon" />
      <p :class="$style.emptyText">
        {{ searchQuery || filterPresetId ? 'Метки не найдены' : 'Нет меток для отображения' }}
      </p>
      <btn @click="handleAdd">
        {{ $t('action.add') }} метки
      </btn>
    </div>

    <div v-if="isLoading" :class="$style.loading">
      <spinner />
      <span>Загрузка меток...</span>
    </div>

    <div :class="$style.addButton">
      <btn data-testid="add-label-btn" @click="handleAdd">
        {{ $t('action.add') }} метки
      </btn>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SimpleTable from '@/ui-kit/SimpleTable';
import SelectInput from '@/ui-kit/SelectInput';
import TextInput from '@/ui-kit/TextInput';
import Btn from '@/ui-kit/Btn';
import Icon from '@/ui-kit/Icon';
import Spinner from '@/ui-kit/Spinner';

export default {
  name: 'Top100LabelsTable',
  components: {
    SimpleTable,
    SelectInput,
    TextInput,
    Btn,
    Icon,
    Spinner,
  },
  props: {
    presets: {
      type: Array,
      default: () => [],
    },
    presetsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      columns: [
        {
          id: 'preset',
          label: 'Пресет видимости',
          width: '25%',
          rowCellSlot: true,
        },
        {
          id: 'labels',
          label: 'Метки',
          width: '45%',
          rowCellSlot: true,
        },
        {
          id: 'created_at',
          label: 'Дата создания',
          width: '20%',
          rowCellSlot: true,
        },
        {
          id: 'actions',
          label: 'Действия',
          width: '10%',
          rowCellSlot: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters('top100Labels', [
      'filteredLabels',
      'isLoading',
      'error',
      'filterPresetId',
      'searchQuery',
    ]),
    
    projectId() {
      return this.$route.params.projectId;
    },
    
    presetOptions() {
      return this.presets.map(preset => ({
        id: preset.id,
        title: this.getPresetName(preset.id)
      }));
    },
  },
  methods: {
    ...mapActions('top100Labels', [
      'fetchLabels',
      'deleteLabel',
      'setFilterPresetId',
      'setSearchQuery',
    ]),
    
    getPresetName(presetId) {
      const preset = this.presets.find(p => p.id === presetId);
      if (!preset) return `Пресет #${presetId}`;
      
      // Формируем название пресета из доступных данных
      const searchEngine = preset.search_engine === 'yandex' ? 'Яндекс' : 'Google';
      const device = preset.device === 'desktop' ? 'Десктоп' : 'Мобильный';
      const region = preset.pos_region || 'Неизвестно';
      
      return `${region}, ${device} - ${searchEngine}`;
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Не указано';
      
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (error) {
        return 'Неверная дата';
      }
    },
    
    handlePresetFilter(presetId) {
      this.setFilterPresetId(presetId);
    },
    
    handleSearch(query) {
      this.setSearchQuery(query);
    },
    
    handleRowClick(row) {
      this.$emit('edit', row);
    },
    
    handleEdit(row) {
      this.$emit('edit', row);
    },
    
    handleAdd() {
      this.$emit('add');
    },
    
    async handleDelete(row) {
      if (confirm('Вы уверены, что хотите удалить эти метки?')) {
        try {
          await this.deleteLabel({
            projectId: this.projectId,
            labelId: row.id,
          });
          this.$emit('deleted');
        } catch (error) {
          console.error('Ошибка удаления меток:', error);
        }
      }
    },
  },
};
</script>

<style lang="scss" module>
@import "~@/ui-kit/_theme/main.scss";

.root {
  background: white;
  border-radius: $border-radius-block;
  padding: $gap-m;
}


.labelsContainer {
  display: flex;
  flex-wrap: wrap;
  gap: $gap-xs;
}

.labelTag {
  display: inline-block;
  background: $bg-accented-color;
  color: $text-primary-color;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid $line-color;
}

.noLabels {
  color: $text-secondary-color;
  font-style: italic;
  font-size: 14px;
}

.dateText {
  color: $text-primary-color;
  font-size: 14px;
  white-space: nowrap;
}

.presetName {
  color: $text-primary-color;
  font-size: 14px;
  font-weight: 500;
}

.actions {
  display: flex;
  gap: $gap-xs;
  align-items: center;
}

.actionBtn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 16px;
  transition: all 0.2s ease;
  color: $text-secondary-color;
  opacity: 0.7;
  
  &:hover {
    background-color: $bg-accented-color;
    color: $text-primary-color;
    opacity: 1;
  }
}

.emptyState {
  text-align: center;
  padding: $gap-xl;
  color: $text-secondary-color;
}

.emptyIcon {
  width: 48px;
  height: 48px;
  margin-bottom: $gap-m;
  opacity: 0.5;
}

.emptyText {
  margin-bottom: $gap-m;
  font-size: 16px;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $gap-s;
  padding: $gap-xl;
  color: $text-secondary-color;
}

.addButton {
  margin-top: $gap-m;
  text-align: right;
}
</style>
